/**
 * Month — `docs/activity.md` §6.4.3 (Board 4 B, Sunday first): each day lists
 * the distinct cities visited in time order (two, then *+n more*), days a
 * person does not work shaded, days gone a shade greyer, and today and the
 * six after carry the forecast's icon and high. With several people each
 * city's dot is the person's colour (§19 B4).
 */
import { KIND_STYLES, WEATHER_COLOURS, WEATHER_TITLES, monthWeeks, toneOf, type CalendarAnswer } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { PAST_TINT } from './look.js'

const NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

export function MonthGrid({
  answer, date, today, onOpenDay,
}: { answer: CalendarAnswer; date: string; today: string; onOpenDay: (day: string) => void }) {
  const weeks = monthWeeks(date)
  const month = date.slice(0, 7)
  const several = answer.people.length > 1

  return (
    /* The month fills the page (Aaron, Oct 23): its weeks share the height, never shorter than drawn. */
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-xl border border-line bg-surface" data-grid="month">
      <div className="flex shrink-0">
        {NAMES.map((name) => (
          <div key={name} className="min-w-0 flex-1 px-2 py-1.5 text-[10.5px] font-semibold uppercase text-text-muted">{name}</div>
        ))}
      </div>
      {weeks.map((week) => (
        <div key={week[0]} className="flex min-h-[84px] flex-1">
          {week.map((day) => {
            const gone = day < today
            const off = answer.people.every((p) => {
              const own = p.days.find((d) => d.date === day)
              return own ? !own.working : false
            })
            /* Distinct cities in time order, each with whose dot. */
            const seen = new Set<string>()
            const cities: { city: string; colour: string }[] = []
            const stops = answer.people.flatMap((p) => (p.days.find((d) => d.date === day)?.items ?? [])
              .filter((item) => (item.kind === 'appointment' || item.kind === 'meeting') && item.place?.city)
              /* One person: every city's dot is green (§6.4.3, Board 4 B); several: the person's colour. */
              .map((item) => ({ item, colour: several ? toneOf(p.colour).edge : KIND_STYLES.site_visit.fg })))
              .sort((a, b) => a.item.start - b.item.start)
            for (const { item, colour } of stops) {
              const city = item.place!.city!
              if (seen.has(city)) continue
              seen.add(city)
              cities.push({ city, colour })
            }
            const weather = answer.dates.find((d) => d.date === day)?.weather
            return (
              <button key={day} type="button" data-cell={day} onClick={() => onOpenDay(day)}
                /* A button centres what it holds; Board 4 B's day starts at the top. */
                className={cn('flex min-h-[84px] min-w-0 flex-1 flex-col justify-start overflow-hidden border-l border-t border-line-faint px-[7px] py-[5px] text-left first:border-l-0',
                  off && 'bg-surface-alt')}
                style={{
                  boxShadow: gone ? `inset 0 0 0 999px ${PAST_TINT}` : undefined,
                  background: day === today ? 'rgb(var(--color-green-band-bg))' : undefined,
                }}>
                <div className="flex items-center gap-1">
                  {/* The day the arrows are on, when it is not today: ringed, as in a day's head. */}
                  {day === date && day !== today ? (
                    <b data-selected-day="" className="-ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full border-[1.5px] border-accent px-1 text-[12px] text-accent-dark">
                      {Number(day.slice(8))}
                    </b>
                  ) : (
                    <b className={cn('text-[12px]', day.slice(0, 7) !== month ? 'text-text-faint'
                      : day === today ? 'text-accent' : gone ? 'text-text-muted' : 'text-text')}>
                      {Number(day.slice(8))}
                    </b>
                  )}
                  <span className="flex-1" />
                  {weather && (
                    <span className="inline-flex items-center gap-1 text-[11px]">
                      <span className="inline-flex" style={{ color: WEATHER_COLOURS[weather.icon] }}>
                        <Glyph name={weather.icon} size={13} title={WEATHER_TITLES[weather.icon]} />
                      </span>
                      {weather.high !== null && <b className="font-bold">{weather.high}°</b>}
                    </span>
                  )}
                </div>
                {cities.slice(0, 2).map(({ city, colour }) => (
                  <div key={city} className="mt-0.5 flex items-center gap-1 truncate whitespace-nowrap text-[10.5px] font-semibold text-text-body">
                    <i className="inline-block h-[5px] w-[5px] shrink-0 rounded-full" style={{ background: colour }} />{city}
                  </div>
                ))}
                {cities.length > 2 && (
                  <div className="mt-0.5 text-[10.5px] font-semibold text-text-faint">+{cities.length - 2} more</div>
                )}
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}
